// Pipe tool (owner, 2026-10-06): click two centre points to draw a pipe between them. A click on
// a free fitting or pipe end attaches the pipe there (joined end, the end's size). A typed length
// + Enter after the first click makes a pipe that long toward the cursor. The chain goes on from
// the end just made; Esc or right-click ends it, Esc again = Select.
//
// Also the small shared helpers the Select tool, the Inspector and the ToolManager use for pipes
// (port markers, join patches, names). The pipe ENGINE is src/pipes/api.ts; this file only calls it.
import { angleOf, dir, dist, sub, type Vec } from "../core/geometry";
import { formatLength, parseLengthAngle } from "../core/units";
import type { Doc, Entity } from "../doc/entities";
import type { Command } from "../doc/commands";
import type { RenderView } from "../engine/renderEntities";
import {
  allPorts,
  collarThickness,
  isFitting,
  isPipe,
  joinedEndStyle,
  makePipeCommand,
  pipeMetaOf,
  portsOf,
  setPipePatches,
  type Patch,
  type PipeAnchor,
  type PipeEndStyle,
  type Port
} from "../pipes/api";
import { drawReadout } from "./line";
import { lintPotName, type LintPotSpec } from "../library/lintPots";
import { positionWords } from "../library/butterflyValves";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

/** Sizes offered for new pipes, mm (the fitting sizes; 65 added in 3.24.0, owner 2026-10-23). */
export const PIPE_TOOL_SIZES = [40, 50, 65, 80, 100, 125, 150, 200, 250] as const;

/** The Pipe tool's remembered settings: size of a new pipe and the look of its free ends. */
export interface PipePrefs {
  size: number;
  end: "flat" | "circle";
}
export const DEFAULT_PIPE_PREFS: PipePrefs = { size: 100, end: "flat" };

/** Port search radius in screen pixels. Snapping lands exactly on a port; this allows a little slack. */
const PORT_PX = 10;

/**
 * Engine calls on the passive paths (overlay, Inspector, grips) go through here so a drawing with
 * fittings keeps working while the engine is not built yet: a throw reads as "not a pipe".
 */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** A pipe group. Checks meta first, so ordinary groups never reach the engine. */
export function looksLikePipe(e: Entity | undefined): boolean {
  return e?.type === "group" && !!e.meta?.pipe && safe(() => isPipe(e), false);
}

/** A fitting group with ends. Checks meta first, so ordinary groups never reach the engine. */
export function looksLikeFitting(e: Entity | undefined): boolean {
  return e?.type === "group" && !!(e.meta?.fitting || e.meta?.ports) && safe(() => isFitting(e), false);
}

/** Ports of one fitting or pipe; empty for anything else (and while the engine is not built). */
export function safePorts(doc: Doc, id: string): Port[] {
  const e = doc.entities[id];
  if (!looksLikePipe(e) && !looksLikeFitting(e)) return [];
  return safe(() => portsOf(doc, id), []);
}

/**
 * A port that can take a new pipe: no pipe attached to it, and, for the end of a pipe, that end
 * is not itself attached to something (a joined pipe end sits on a fitting end).
 */
export function portIsFree(doc: Doc, port: Port): boolean {
  if (port.taken) return false;
  if (port.ownerKind !== "pipe") return true;
  const m = pipeMetaOf(doc.entities[port.ownerId]);
  return !!m && (port.index === 0 ? m.a : m.b).kind === "free";
}

/**
 * The nearest FREE port within `tol` of p, skipping owners in `exclude`; null when none. 3.24.0: a VERTICAL end (the
 * circle of a riser or drop) counts as its whole circle, pipe and collar: a point inside the circle is on the end.
 */
export function freePortNear(doc: Doc, p: Vec, tol: number, exclude?: Set<string>, alsoOk?: (port: Port) => boolean): Port | null {
  let best: Port | null = null;
  let bestD = tol;
  for (const port of allPorts(doc)) {
    if (exclude?.has(port.ownerId)) continue;
    const d = port.vertical ? Math.max(0, dist(port.p, p) - (port.size / 2 + collarThickness(port.size))) : dist(port.p, p);
    if (d > bestD) continue;
    if (!portIsFree(doc, port) && !alsoOk?.(port)) continue;
    best = port;
    bestD = d;
  }
  return best;
}

/** "Bend 90° · 100 mm", "Tee · 80 mm, branch 50 mm", "Reducer · 150 → 100 mm", "Davey CM 125x100-200 · 55 kW · side", "pipe", "fitting". */
export function ownerName(doc: Doc, id: string): string {
  const e = doc.entities[id];
  if (looksLikePipe(e)) {
    const m = safe(() => pipeMetaOf(e), null);
    return m ? `pipe ${m.size} mm` : "pipe";
  }
  const pump = e?.meta?.pump as { series?: string; model?: string; kW?: number; view?: string } | undefined;
  if (pump?.series && pump.model) return `Davey ${pump.series} ${pump.model}${typeof pump.kW === "number" ? ` · ${pump.kW} kW` : ""}${pump.view ? ` · ${pump.view}` : ""}`;
  // 3.29.0: equipment from a datasheet (a chlorinator)
  const eq = e?.meta?.equipment as { maker?: string; model?: string; view?: string } | undefined;
  if (eq?.maker && eq.model) return `${eq.maker} ${eq.model}${eq.view ? ` · ${eq.view === "side" ? "end view" : "plan"}` : ""}`;
  const f = e?.meta?.fitting as { kind?: string; size?: number; size2?: number } | undefined;
  if (!f || typeof f.size !== "number") return "fitting";
  const s2 = typeof f.size2 === "number" && f.size2 !== f.size ? f.size2 : null;
  switch (f.kind) {
    case "bend90":
      return `Bend 90° · ${f.size} mm`;
    case "bend45":
      return `Bend 45° · ${f.size} mm`;
    case "tee":
      return s2 ? `Tee · ${f.size} mm, branch ${s2} mm` : `Tee · ${f.size} mm`;
    case "teeup":
    case "teedown": {
      const way = f.kind === "teeup" ? "up" : "down";
      return s2 ? `Tee · ${f.size} mm, branch ${s2} mm ${way}` : `Tee · ${f.size} mm · branch ${way}`;
    }
    case "bendup":
    case "benddown":
      return `Bend 90° · ${f.size} mm · ${f.kind === "bendup" ? "up" : "down"}`;
    case "cap":
      return `End cap · ${f.size} mm`;
    case "reducer":
      return `Reducer · ${f.size} → ${s2 ?? f.size} mm`;
    case "checkvalve": {
      const v = e?.meta?.valve as { D?: number } | undefined;
      return `Check valve · DN${f.size}${typeof v?.D === "number" ? ` · D${v.D}` : ""} · mounting kit`;
    }
    case "butterfly": {
      const b = e?.meta?.butterfly as { operator?: string; open?: boolean; openPct?: number; view?: string } | undefined;
      // 3.22.0: how far open; a valve placed before 3.22.0 has open yes or no only
      const pct = typeof b?.openPct === "number" ? b.openPct : b?.open === false ? 0 : 100;
      return b?.operator ? `Butterfly valve · DN${f.size} · ${b.operator} · ${positionWords(pct)}${b.view ? ` · ${b.view}` : ""}` : `Butterfly valve · DN${f.size}`;
    }
    case "lintpot": {
      const lp = e?.meta?.lintpot as LintPotSpec | undefined;
      return lp && typeof lp.diameter === "number" && Array.isArray(lp.ends) ? safe(() => lintPotName(lp), "Lint pot") : "Lint pot";
    }
    default:
      return "fitting";
  }
}

/** The end style of port `index` (0 = end A, 1 = end B) of a pipe, or null for a non-pipe. */
function pipeEndStyle(doc: Doc, ownerId: string, index: number): PipeEndStyle | null {
  const m = pipeMetaOf(doc.entities[ownerId]);
  if (!m) return null;
  return index === 0 ? m.endA : m.endB;
}

/**
 * A new pipe end joins the end of another PIPE: that pipe's end is hidden (style "none") so no
 * line is drawn across the joint. Empty for a fitting port or an end that is already joined.
 */
export function joinPatches(doc: Doc, ownerId: string, index: number): Patch[] {
  if (!looksLikePipe(doc.entities[ownerId])) return [];
  const style = pipeEndStyle(doc, ownerId, index);
  if (style === null || style === "none") return [];
  return setPipePatches(doc, ownerId, index === 0 ? { endA: "none" } : { endB: "none" });
}

/** The opposite of joinPatches: a pipe end that loses its partner gets a flat end again. */
export function unjoinPatches(doc: Doc, ownerId: string, index: number): Patch[] {
  if (!looksLikePipe(doc.entities[ownerId])) return [];
  if (pipeEndStyle(doc, ownerId, index) !== "none") return [];
  return setPipePatches(doc, ownerId, index === 0 ? { endA: "flat" } : { endB: "flat" });
}

/** One command from patch lists and commands, in order (null when there is nothing). */
export function combine(label: string, parts: (Patch[] | Command | null)[]): Command | null {
  const commands: Command[] = [];
  for (const part of parts) {
    if (!part) continue;
    if (Array.isArray(part)) {
      if (part.length) commands.push({ kind: "updateEntities", patches: part });
    } else commands.push(part);
  }
  if (commands.length === 0) return null;
  return commands.length === 1 ? commands[0]! : { kind: "batch", label, commands };
}

/**
 * Port markers: a ring on the end point (filled when a pipe is attached) and a short tick along
 * the direction a pipe leaves. Screen-constant sizes. `hot` gets a bigger accent ring.
 */
export function drawPorts(v: RenderView, ports: Port[], hot: Port | null = null): void {
  const { ctx } = v;
  const px = (n: number): number => n / v.cam.scale;
  ctx.save();
  ctx.setLineDash([]);
  for (const p of ports) {
    const isHot = !!hot && hot.ownerId === p.ownerId && hot.index === p.index;
    const r = px(isHot ? 7 : 5);
    const colour = p.taken ? "#4caf7d" : "#f0a030";
    // tick along dir; a vertical end (the top of a pump plan) gets a second ring instead: a pipe there goes any way
    ctx.strokeStyle = colour;
    ctx.lineWidth = px(1.5);
    ctx.beginPath();
    if (p.vertical) ctx.arc(p.p.x, p.p.y, r + px(4), 0, Math.PI * 2);
    else {
      ctx.moveTo(p.p.x + p.dir.x * r, p.p.y + p.dir.y * r);
      ctx.lineTo(p.p.x + p.dir.x * (r + px(8)), p.p.y + p.dir.y * (r + px(8)));
    }
    ctx.stroke();
    // ring: white halo, then colour
    ctx.lineWidth = px(3);
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.beginPath();
    ctx.arc(p.p.x, p.p.y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = px(isHot ? 2 : 1.5);
    ctx.strokeStyle = isHot ? "#4dd0e1" : colour;
    ctx.beginPath();
    ctx.arc(p.p.x, p.p.y, r, 0, Math.PI * 2);
    ctx.stroke();
    if (p.taken) {
      ctx.fillStyle = colour;
      ctx.fill();
    }
  }
  ctx.restore();
}

/** Two side lines and the dash-dot centre line of a pipe of `size` from a to b (world units). */
export function drawPipeGhost(v: RenderView, a: Vec, b: Vec, size: number, colour: string): void {
  const len = dist(a, b);
  if (len < 1e-9) return;
  const { ctx } = v;
  const px = (n: number): number => n / v.cam.scale;
  const n = { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
  const h = size / 2;
  ctx.save();
  ctx.strokeStyle = colour;
  ctx.globalAlpha *= 0.85;
  ctx.lineWidth = px(1.5);
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(a.x + n.x * h, a.y + n.y * h);
  ctx.lineTo(b.x + n.x * h, b.y + n.y * h);
  ctx.moveTo(a.x - n.x * h, a.y - n.y * h);
  ctx.lineTo(b.x - n.x * h, b.y - n.y * h);
  ctx.stroke();
  ctx.lineWidth = px(0.75);
  ctx.setLineDash([px(12), px(3), px(2), px(3)]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

/** Where the next pipe starts. */
interface Start {
  p: Vec;
  anchor: PipeAnchor;
  style: PipeEndStyle;
  size: number;
  /** the port the start is attached to (joins a pipe end), or null for a free start */
  port: { ownerId: string; index: number } | null;
  /**
   * 3.11.0: a pipe that starts at a FITTING or PUMP end goes straight out of that end. The next point is taken on this
   * line (the pipe cannot leave a fitting at an angle); only the length is chosen. Unset for a free start or a pipe end.
   */
  axis?: Vec;
}

export class PipeTool implements Tool {
  readonly id = "pipe" as const;
  private start: Start | null = null;
  private cursor: Vec | null = null;
  private hover: Port | null = null;

  constructor(private ctx: ToolCtx) {}

  private settings(): PipePrefs {
    return this.ctx.prefs().pipe ?? DEFAULT_PIPE_PREFS;
  }

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.start !== null;
  }

  private reset(): void {
    this.start = null;
    this.cursor = null;
    this.hover = null;
    this.ctx.setSnapBase(null);
    this.ctx.setSelfClose(null);
    this.ctx.setMeasure("length", "pipe length or length<angle");
    this.ctx.setHint("Pipe: click the first centre point (a fitting end attaches) · Esc = Select");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  private sync(): void {
    if (!this.start) {
      this.reset();
      return;
    }
    this.ctx.setSnapBase(this.start.p);
    this.ctx.setHint(
      this.start.axis
        ? `Pipe ${this.start.size} mm out of the fitting end: click or type the length ⏎ · right-click the new pipe end for a fitting · Esc = end`
        : `Pipe ${this.start.size} mm: click the next centre point — type a length ⏎ · Shift = lock axis · right-click the pipe end for a fitting · Esc = end`
    );
    this.ctx.setCursorTip("next point · length ⏎");
    this.ctx.requestRender();
  }

  private tol(): number {
    return PORT_PX / this.ctx.scale();
  }

  /** A FREE port at p, or null. */
  private freePortAt(p: Vec): Port | null {
    return freePortNear(this.ctx.store.doc, p, this.tol());
  }

  /** Where the pipe would end for the pointer at p: on the line out of a fitting end, else p itself. */
  private target(p: Vec): Vec {
    const s = this.start;
    if (!s?.axis) return p;
    const len = Math.max(0, (p.x - s.p.x) * s.axis.x + (p.y - s.p.y) * s.axis.y);
    return { x: s.p.x + s.axis.x * len, y: s.p.y + s.axis.y * len };
  }

  /**
   * Start a pipe at a free end of a fitting, a pump or a pipe (3.11.0): the right-click menu of a pipe end places a
   * fitting and the run goes on from the fitting's other end at once. False when that end is not free.
   */
  beginAt(ownerId: string, index: number): boolean {
    const doc = this.ctx.store.doc;
    const port = portsOf(doc, ownerId).find((p) => p.index === index);
    if (!port || !portIsFree(doc, port)) return false;
    this.start = {
      p: port.p,
      anchor: { kind: "port", ownerId, port: index },
      style: joinedEndStyle(port),
      size: port.size,
      port: { ownerId, index },
      // a vertical end (the top of a pump on a plan) has no axis on the plan: the pipe goes any way
      ...(port.ownerKind === "fitting" && !port.vertical ? { axis: { ...port.dir } } : {})
    };
    this.sync();
    return true;
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button === 2) {
      if (this.start) this.reset();
      return;
    }
    if (e.button !== 0) return;
    const port = this.freePortAt(e.point);
    if (!this.start) {
      const pref = this.settings();
      this.start = port
        ? {
            p: port.p,
            anchor: { kind: "port", ownerId: port.ownerId, port: port.index },
            style: joinedEndStyle(port),
            size: port.size,
            port: { ownerId: port.ownerId, index: port.index },
            ...(port.ownerKind === "fitting" && !port.vertical ? { axis: { ...port.dir } } : {})
          }
        : { p: e.point, anchor: { kind: "free", p: e.point }, style: pref.end, size: pref.size, port: null };
      this.sync();
      return;
    }
    this.commit(port ? port.p : this.target(e.point), port);
  }

  /** Make the pipe from the start to p (attached to `port` when given). */
  private commit(p: Vec, port: Port | null): void {
    const s = this.start;
    if (!s) return;
    if (dist(s.p, p) < 1e-6) return; // zero-length pipe ignored
    const doc = this.ctx.store.doc;
    const pref = this.settings();
    const made = makePipeCommand(doc, this.ctx.activeLayerId(), {
      size: s.size,
      a: s.anchor,
      b: port ? { kind: "port", ownerId: port.ownerId, port: port.index } : { kind: "free", p },
      endA: s.style,
      endB: port ? joinedEndStyle(port) : pref.end
    });
    const cmd = combine("pipe", [
      s.port ? joinPatches(doc, s.port.ownerId, s.port.index) : null,
      port ? joinPatches(doc, port.ownerId, port.index) : null,
      made.cmd
    ]);
    if (cmd) this.ctx.store.dispatch(cmd);
    if (port) {
      // the pipe ended on a fitting or pipe end: the run is complete
      this.reset();
      this.ctx.setHint("Pipe attached. Click the first centre point of the next pipe · Esc = Select");
      return;
    }
    this.start = {
      p,
      anchor: { kind: "port", ownerId: made.groupId, port: 1 },
      style: "none",
      size: s.size,
      port: { ownerId: made.groupId, index: 1 }
    };
    this.sync();
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.hover = this.freePortAt(e.point);
    if (this.hover) this.ctx.setCursorTip(`end · ${this.hover.size} mm · ${this.hover.label}`);
    else this.ctx.setCursorTip(this.start ? "next point · length ⏎" : null);
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {
    if (this.start) this.reset();
  }

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape") {
      if (this.start) this.reset();
      else this.ctx.switchTool("select");
      return true;
    }
    if (e.key === "Enter" && this.start) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    const s = this.start;
    if (!s) return false;
    const parsed = parseLengthAngle(raw, this.ctx.docUnits().units);
    if (!parsed || !(parsed.length > 0)) return false;
    let d: Vec;
    if (parsed.angleDeg !== null) d = dir(parsed.angleDeg);
    else if (s.axis) d = s.axis; // straight out of the fitting end
    else {
      const toward = this.cursor && dist(this.cursor, s.p) > 1e-9 ? this.cursor : null;
      d = toward ? dir(angleOf(sub(toward, s.p))) : dir(0);
    }
    this.commit({ x: s.p.x + d.x * parsed.length, y: s.p.y + d.y * parsed.length }, null);
    return true;
  }

  renderDynamic(v: RenderView): void {
    const s = this.start;
    if (!s || !this.cursor) return;
    const end = this.hover ? this.hover.p : this.target(this.cursor);
    drawPipeGhost(v, s.p, end, s.size, this.ctx.store.doc.defaultStyle.strokeColor);
    const len = dist(s.p, end);
    if (len > 1e-9) {
      const { units, precision } = this.ctx.docUnits();
      drawReadout(v, end, `${formatLength(len, units, precision)}  ${angleOf(sub(end, s.p)).toFixed(1)}°  ⌀${s.size}`);
    }
  }

  renderOverlay(v: RenderView): void {
    if (!this.cursor) return;
    const near = 120 / this.ctx.scale();
    const c = this.cursor;
    const ports = allPorts(this.ctx.store.doc).filter((p) => dist(p.p, c) <= near);
    if (ports.length) drawPorts(v, ports, this.hover);
  }
}
